import { Box, CircularProgress, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { OpenShiftCardContainer } from "@src/appV2/redesign/Shift/Open/CardContainer";
import { type RateNegotiation } from "@src/appV2/redesign/Shift/Open/types";
import { OpenShiftListDataProvider } from "@src/appV2/redesign/Shift/Open/useOpenShiftListDataContext";
import { SHIFT_DISCOVERY_SHIFT_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { CbhIcon, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useEffect, useMemo } from "react";

import { Button } from "../../components/Button";
import { useGetFcfReplacementShifts } from "./hooks/useGetFcfReplacementShifts";
import { type FcfDeepLinkParams } from "./types";

const EMPTY_RATE_NEGOTIATIONS_MAP = new Map<string, RateNegotiation>();

interface FcfReplacementShiftsBottomSheetProps {
  modalState: UseModalState;
  onDismiss: () => void;
  onInteract?: () => Promise<void>;
  onNoEligibleShifts?: () => void;
  params: FcfDeepLinkParams;
}

export function FcfReplacementShiftsBottomSheet(props: FcfReplacementShiftsBottomSheetProps) {
  const { modalState, onDismiss, onInteract, onNoEligibleShifts, params } = props;
  const { navigateToModal } = useShiftModalsDataContext();

  const { replacementShifts, isLoading, isSuccess } = useGetFcfReplacementShifts({
    ...params,
    enabled: modalState.modalIsOpen,
  });

  const isEmpty = isSuccess && replacementShifts.length === 0;

  useEffect(() => {
    if (isEmpty) {
      onNoEligibleShifts?.();
    }
    // onNoEligibleShifts is intentionally excluded: the callback identity is not
    // stable across renders, but we only want to fire once when isEmpty first
    // becomes true, not every time the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty]);

  const workplacesMap = useMemo(
    () => new Map(replacementShifts.map(({ workplace }) => [workplace.id, workplace])),
    [replacementShifts]
  );

  function handleShiftTap(shiftId: string): void {
    void onInteract?.();
    navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
      clearExistingSearchParams: true,
      pathParams: { shiftId },
    });
  }

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <Box sx={{ display: "flex", justifyContent: "flex-end", paddingX: 5, marginTop: 4 }}>
        <IconButton
          iconType="close"
          aria-label="Close"
          variant="outlined"
          size="medium"
          onClick={onDismiss}
        />
      </Box>
      {!isEmpty && (
        <Stack spacing={2} sx={{ paddingX: 6, marginTop: 4, marginBottom: 4 }}>
          <Text
            textAlign="center"
            variant="body1"
            sx={{ color: (theme) => theme.palette.text.secondary }}
          >
            We're sorry your shift was cancelled
          </Text>
          <Text
            textAlign="center"
            variant="h4"
            sx={{ color: (theme) => theme.palette.text?.primary }}
          >
            Here are some nearby shifts starting at the same time
          </Text>
        </Stack>
      )}
      <DialogContent>
        {isLoading && (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              justifyContent: "center",
              paddingY: 10,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {isEmpty && (
          <Stack alignItems="center" spacing={8} height="100%" justifyContent="center">
            <CbhIcon size="xLarge" type="search" />
            <Text
              textAlign="center"
              variant="body1"
              sx={{ color: (theme) => theme.palette.text?.secondary ?? "" }}
            >
              Looks like all replacement shifts have since been booked, sorry about that!
            </Text>
          </Stack>
        )}

        {!isLoading && !isEmpty && (
          <OpenShiftListDataProvider
            openShifts={replacementShifts.map(({ shift }) => shift)}
            workplacesMap={workplacesMap}
            rateNegotiationsMap={EMPTY_RATE_NEGOTIATIONS_MAP}
          >
            <Stack spacing={3}>
              {replacementShifts.map(({ shift, workplace }) => (
                <OpenShiftCardContainer
                  key={shift.id}
                  shift={shift}
                  workplace={workplace}
                  workers={[]}
                  onClick={() => {
                    handleShiftTap(shift.id);
                  }}
                />
              ))}
            </Stack>
          </OpenShiftListDataProvider>
        )}
      </DialogContent>

      <DialogFooter withShadow={replacementShifts.length >= 3} orientation="horizontal">
        <Button fullWidth size="large" variant="outlinedHeavy" onClick={onDismiss}>
          {isEmpty ? "Okay" : "I'm not interested"}
        </Button>
      </DialogFooter>
    </FullScreenDialog>
  );
}
